import {
  DividerHorizontalIcon,
  FileIcon,
  ImageIcon,
  TextIcon,
  ViewVerticalIcon,
  VideoIcon,
  ChatBubbleIcon,
  ClipboardIcon
} from "@radix-ui/react-icons";

import * as cheerio from "cheerio";

export const PUBLISHED_STATUS_DRAFT = "draft";
export const PUBLISHED_STATUS_PUBLISHED = "published";

export const PUBLISHED_STATUSES = {
  [PUBLISHED_STATUS_DRAFT]: "Draft",
  [PUBLISHED_STATUS_PUBLISHED]: "Published"
};

export const PUBLISHED_STATUS_COLORS = {
  [PUBLISHED_STATUS_DRAFT]: "bg-yellow-100 text-yellow-800",
  [PUBLISHED_STATUS_PUBLISHED]: "bg-green-100 text-green-800"
};

export const CONTENT_TYPE_TEXT_LEFT = "text-only-left";
export const CONTENT_TYPE_TEXT_RIGHT = "text-only-right";
export const CONTENT_TYPE_TEXT_CENTER = "text-only-center";
export const CONTENT_TYPE_TEXT_IMAGE_LEFT = "text-with-left-image";
export const CONTENT_TYPE_TEXT_IMAGE_RIGHT = "text-with-right-image";
export const CONTENT_TYPE_IMAGE_LEFT = "image-only-left";
export const CONTENT_TYPE_IMAGE_RIGHT = "image-only-right";
export const CONTENT_TYPE_IMAGE_CENTER = "image-only-center";
export const CONTENT_TYPE_IMAGE_FULL = "image-only-full";
export const CONTENT_TYPE_DIVIDER = "divider";
export const CONTENT_TYPE_MUX_VIDEO = "mux-video";
export const CONTENT_TYPE_TESTIMONIAL = "testimonial";

export const CONTENT_TYPE_TEXT = "text";
export const CONTENT_TYPE_IMAGE = "image";
export const CONTENT_TYPE_TEXT_IMAGE = "text-image";
export const CONTENT_TYPE_FILE = "file";
export const CONTENT_TYPE_FORM = "form";

export const CONTENT_TYPES = {
  [CONTENT_TYPE_TEXT_LEFT]: "Text-only, Left-aligned",
  [CONTENT_TYPE_TEXT_RIGHT]: "Text-only, Right-aligned",
  [CONTENT_TYPE_TEXT_CENTER]: "Text-only, Center-aligned",
  [CONTENT_TYPE_TEXT_IMAGE_LEFT]: "Text with Image on Left",
  [CONTENT_TYPE_TEXT_IMAGE_RIGHT]: "Text with Image on Right",
  [CONTENT_TYPE_IMAGE_LEFT]: "Image-only, Left-aligned",
  [CONTENT_TYPE_IMAGE_RIGHT]: "Image-only, Right-aligned",
  [CONTENT_TYPE_IMAGE_CENTER]: "Image-only, Center-aligned",
  [CONTENT_TYPE_IMAGE_FULL]: "Image-only, Full Width",
  [CONTENT_TYPE_DIVIDER]: "Divider",
  [CONTENT_TYPE_MUX_VIDEO]: "Video (Mux)",
  [CONTENT_TYPE_TESTIMONIAL]: "Testimonial",
  [CONTENT_TYPE_FORM]: "Form"
};

export const CONTENT_TYPES_NEW = {
  [CONTENT_TYPE_TEXT]: "Text",
  [CONTENT_TYPE_IMAGE]: "Image",
  [CONTENT_TYPE_TEXT_IMAGE]: "Text with Image",
  [CONTENT_TYPE_FILE]: "File",
  [CONTENT_TYPE_FORM]: "Form",
  [CONTENT_TYPE_DIVIDER]: "Divider",
  [CONTENT_TYPE_MUX_VIDEO]: "Video (Mux)",
  [CONTENT_TYPE_TESTIMONIAL]: "Testimonial"
};

export const CONTENT_TYPE_ICONS = {
  [CONTENT_TYPE_TEXT]: TextIcon,
  [CONTENT_TYPE_IMAGE]: ImageIcon,
  [CONTENT_TYPE_TEXT_IMAGE]: ViewVerticalIcon,
  [CONTENT_TYPE_FILE]: FileIcon,
  [CONTENT_TYPE_FORM]: ClipboardIcon,
  [CONTENT_TYPE_DIVIDER]: DividerHorizontalIcon,
  [CONTENT_TYPE_MUX_VIDEO]: VideoIcon,
  [CONTENT_TYPE_TESTIMONIAL]: ChatBubbleIcon
};

export const COLOR_THEMES = [
  "black",
  "white",
  "rose",
  "pink",
  "fuchsia",
  "purple",
  "violet",
  "indigo",
  "blue",
  "sky",
  "cyan",
  "teal",
  "emerald",
  "green",
  "lime",
  "yellow",
  "amber",
  "orange",
  "red",
  "stone",
  "neutral",
  "gray",
  "slate"
];

export const translateContentBackendToFrontend = (
  contentType,
  imageAlign,
  textPlacement
) => {
  if (contentType === CONTENT_TYPE_TEXT) return CONTENT_TYPE_TEXT_LEFT;
  if (contentType === CONTENT_TYPE_IMAGE) {
    if (imageAlign === "center") return CONTENT_TYPE_IMAGE_CENTER;
    if (imageAlign === "right") return CONTENT_TYPE_IMAGE_RIGHT;
    if (imageAlign === "full") return CONTENT_TYPE_IMAGE_FULL;
    return CONTENT_TYPE_IMAGE_LEFT;
  }
  if (contentType === CONTENT_TYPE_TEXT_IMAGE) {
    if (textPlacement === "left") return CONTENT_TYPE_TEXT_IMAGE_RIGHT;
    return CONTENT_TYPE_TEXT_IMAGE_LEFT;
  }
  return contentType;
};

export const translateContentFrontendToBackend = (contentType) => {
  if (contentType === "") return "";
  if (contentType === CONTENT_TYPE_TEXT_LEFT) return CONTENT_TYPE_TEXT;
  if (contentType === CONTENT_TYPE_IMAGE_CENTER) return CONTENT_TYPE_IMAGE;
  if (contentType === CONTENT_TYPE_IMAGE_RIGHT) return CONTENT_TYPE_IMAGE;
  if (contentType === CONTENT_TYPE_IMAGE_FULL) return CONTENT_TYPE_IMAGE;
  if (contentType === CONTENT_TYPE_IMAGE_LEFT) return CONTENT_TYPE_IMAGE;
  if (contentType === CONTENT_TYPE_TEXT_IMAGE_RIGHT)
    return CONTENT_TYPE_TEXT_IMAGE;
  if (contentType === CONTENT_TYPE_TEXT_IMAGE_LEFT)
    return CONTENT_TYPE_TEXT_IMAGE;
  return contentType;
};

export const enrichImage = (image, imageUrl, imageAltText) => {
  if (!image) return image;
  return {
    ...image,
    link: imageUrl ? imageUrl : null,
    altText: imageAltText ? imageAltText : null
  };
};

export const prepareSectionsForFrontend = (sections, url, orgData) => {
  return sections.map(
    ({
      heading,
      show_heading,
      heading_level,
      id,
      content_type,
      content,
      image,
      text_placement: textPlacement,
      image_align: imageAlign,
      image_url: imageUrl,
      image_alt_text: imageAltText,
      cta_data: ctaData,
      heading_color_theme: headingColorTheme,
      text_color_theme: textColorTheme,
      background_color_theme: backgroundColorTheme,
      button_color_theme: buttonColorTheme,
      mux_playback_id: muxPlaybackId,
      mux_poster_offset: muxPosterOffset,
      mux_accent_color: muxAccentColor,
      testimonial_ids: testimonialIds,
      form_id: formId
    }) => {
      // parse script tags so that react can handle them as a special case
      const scripts = [];
      if (content) {
        const dom = cheerio.load(content, null, false);
        dom("script").map((_, e) => {
          scripts.push(dom(e).attr("src"));
          dom(e).remove();
        });
        if (scripts.length > 0) content = dom.html();
      }

      return {
        heading,
        show_heading,
        heading_level,
        id,
        content_type: translateContentBackendToFrontend(
          content_type,
          imageAlign,
          textPlacement
        ),
        content,
        image: enrichImage(image, imageUrl, imageAltText),
        scripts,
        headingColorTheme,
        textColorTheme,
        backgroundColorTheme,
        buttonColorTheme,
        muxPlaybackId,
        muxPosterOffset,
        muxAccentColor,
        testimonialIds,
        formId
      };
    }
  );
};
